import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button } from '@base-app';
import { DateRangeOptionId, PreviousPeriodComparison, NumberText, InfoTitle } from '@builder';
import {
  StyledAccentColor,
  StyledBodySamples,
  StyledButtonContainer,
  StyledContent,
  StyledContentSeparator,
  StyledRightArrowIcon,
  StyledTotalDetails,
} from './SampleTotal.styles';
import { SampleTotalProps } from './SampleTotal.types';
import { STUDY_INSIGHTS_TRANSLATION } from '@modules/study-insights/constants';
import { Sizes, Variants, Weights } from '@common/types';
import { useModal } from '@components/hooks';

import SequencedSamplesByCollectionLocation from '../SampleViewGraphByCollectionLocationModal/SampleViewGraphByCollectionLocationModal';

const SampleTotal: FC<SampleTotalProps> = ({
  accentColor,
  collectionLocation,
  description,
  id,
  onClickButton,
  percentage,
  period,
  title,
  totalSamples,
}) => {
  const { t } = useTranslation(STUDY_INSIGHTS_TRANSLATION);
  const { isShowing, toggle: toggleModal } = useModal();

  const handleOnClickGraphButton = (): void => {
    onClickButton?.();
    toggleModal();
  };

  return (
    <Box data-testid={`${id}-total-samples-received`}>
      <InfoTitle testId={`${id}-total-samples-received-title`}>{title && title}</InfoTitle>
      {accentColor && <StyledAccentColor css={{ backgroundColor: accentColor }} />}
      <StyledBodySamples>
        <StyledContent>
          <NumberText
            css={{
              fontSize: !isNaN(Number(totalSamples)) ? '$studyInsights$fontXl' : '$studyInsights$fontXs',
            }}
            testId={`${id}-total-samples-received-total`}
            size={Sizes.LARGE}
            weight={Weights.EXTRA_BOLD}
          >
            {totalSamples}
          </NumberText>
          {description && <StyledTotalDetails>{description}</StyledTotalDetails>}
        </StyledContent>
        {period !== undefined && period !== DateRangeOptionId.ALL_TIME && (
          <>
            <StyledContentSeparator />
            <PreviousPeriodComparison percentage={percentage} period={period} testId={`${id}-total-samples-received`} />
          </>
        )}
      </StyledBodySamples>
      <StyledButtonContainer>
        {onClickButton && (
          <Button
            variant={Variants.SECONDARY}
            onClick={handleOnClickGraphButton}
            css={{ padding: '$1 $4' }}
            id={`${id}-view-graph-format`}
          >
            {t('view_graph_format')}
            <Box css={{ display: 'inline-flex' }}>
              <StyledRightArrowIcon />
            </Box>
          </Button>
        )}
      </StyledButtonContainer>
      {isShowing && collectionLocation && (
        <SequencedSamplesByCollectionLocation collectionLocation={collectionLocation} onClose={toggleModal} />
      )}
    </Box>
  );
};

export default SampleTotal;
